// Underwriting examples: one scheme priced two ways (build to sell against build
// to rent), a hotel and a data centre.
//
// The first is wired to the live project like the rest of the examples. The hotel
// and data centre take their size from the model when the project has a usage by
// that name, and fall back to a typed-in size when it does not — few projects
// will, and an appraisal full of zeros teaches nothing. Their rates are
// placeholders — check each one before relying on the result.

import type { FormatId, Sheet } from '@/model/types';
import type { Example } from './index';
import { addSheet, createWorkbook, setCellRaw } from '@/model/workbook';
import { parseCellA1 } from '@/model/address';

const DISCLAIMER = 'Every rate here is a placeholder. Check each one before relying on the result.';

// The same small helpers as index.ts — kept local so the two files do not import
// each other.

function put(sheet: Sheet, cells: Record<string, string>): void {
  for (const [addr, raw] of Object.entries(cells)) {
    const rc = parseCellA1(addr);
    if (!rc) throw new Error(`bad address ${addr}`);
    setCellRaw(sheet, rc.row, rc.col, raw);
  }
}

function bold(sheet: Sheet, addrs: string[]): void {
  for (const addr of addrs) {
    const rc = parseCellA1(addr)!;
    const key = `${rc.row},${rc.col}`;
    sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
  }
}

function format(sheet: Sheet, range: string, fmt: FormatId): void {
  const [from, to] = range.split(':');
  const a = parseCellA1(from!)!;
  const b = parseCellA1(to ?? from!)!;
  for (let r = Math.min(a.row, b.row); r <= Math.max(a.row, b.row); r++) {
    for (let c = Math.min(a.col, b.col); c <= Math.max(a.col, b.col); c++) {
      const key = `${r},${c}`;
      sheet.cells[key] = { ...sheet.cells[key], fmt };
    }
  }
}

const AREA_UNIT = 'IF(GI.UNITS()="imperial","ft²","m²")';

// ---------------------------------------------------------------------------
// Build to sell vs build to rent
// ---------------------------------------------------------------------------

/**
 * One scheme, two exits, three sheets.
 *
 * Inputs holds everything both exits share — the model's numbers, land, fees,
 * finance — so the two sheets cannot quietly disagree about what the building
 * costs. Each exit sheet reads Inputs and nothing else, and the summary on
 * Inputs reads them back, so the comparison sits beside the assumptions that
 * drive it.
 */
const btsVsBtr: Example = {
  id: 'bts-vs-btr',
  name: 'Build to sell vs build to rent',
  summary: 'Three sheets: shared inputs from the model, a for-sale exit and a ten-year rental hold, compared side by side.',
  build() {
    const wb = createWorkbook();
    const inputs = wb.sheets[0]!;
    inputs.name = 'Inputs';
    const sell = addSheet(wb, 'Build to sell');
    const rent = addSheet(wb, 'Build to rent');

    // ---- Inputs ------------------------------------------------------------
    Object.assign(inputs.colWidths, { 1: 230, 2: 120, 3: 24, 4: 210, 5: 120, 6: 120 });
    put(inputs, {
      A1: 'Build to sell vs build to rent',
      A2: 'Shared inputs. Each exit has its own sheet; the summary reads them back.',

      A4: 'From the model',
      A5: 'Residential usage',
      B5: 'Residential',
      A6: 'Dwellings',
      B6: '=GI.TOTAL("dwellingTotalCount","usage="&B5)',
      A7: 'Gross floor area',
      B7: '=GI.TOTAL("grossArea","usage="&B5)',
      A8: 'Saleable area',
      B8: '=GI.TOTAL("saleableArea","usage="&B5)',
      // Per geometry, then added — not gross area times an average rate.
      A9: 'Construction cost',
      B9: '=GI.SUMEACH("grossArea * hardCost","usage="&B5)',
      A10: 'Gross development value',
      B10: '=GI.SUMEACH("saleableArea * salePrice","usage="&B5)',

      A12: 'Shared assumptions',
      A13: 'Land cost per dwelling',
      B13: '80000',
      A14: 'Land cost',
      B14: '=B6*B13',
      A15: 'Professional fees',
      B15: '10%',
      A16: 'Contingency',
      B16: '5%',
      A17: 'Finance rate (annual)',
      B17: '7%',
      A18: 'Build period (months)',
      B18: '24',
      // Land is drawn on day one; build cost on an S-curve, which averages out
      // at about half drawn across the build.
      A19: 'Finance cost',
      B19: '=(B14+B9*(1+B15+B16)/2)*B17*B18/12',
      A20: 'Total development cost',
      B20: '=B14+B9*(1+B15+B16)+B19',

      A22: 'Build to sell',
      A23: 'Selling costs',
      B23: '2.5%',
      A24: 'Target profit on cost',
      B24: '20%',

      A26: 'Build to rent',
      A27: 'Rent per dwelling per month',
      B27: '3000',
      A28: 'Other income per dwelling per month',
      B28: '100',
      A29: 'Vacancy and bad debt',
      B29: '4%',
      A30: 'Operating costs (% of income)',
      B30: '28%',
      A31: 'Rental growth (annual)',
      B31: '3%',
      A32: 'Exit yield',
      B32: '4.75%',
      A33: 'Disposal costs',
      B33: '1.5%',
      A34: 'Discount rate',
      B34: '7.5%',

      D4: 'Summary',
      E4: 'Sell',
      F4: 'Rent',
      D5: 'Total development cost',
      E5: "=-'Build to sell'!B15",
      F5: "='Build to rent'!B13",
      D6: 'Profit at completion',
      E6: "='Build to sell'!B18",
      F6: "='Build to rent'!B16",
      D7: 'Profit on cost',
      E7: "='Build to sell'!B19",
      F7: "='Build to rent'!B17",
      D8: 'Yield on cost',
      E8: '—',
      F8: "='Build to rent'!B14",
      D9: 'Unlevered IRR',
      E9: '—',
      F9: "='Build to rent'!B26",
      D10: 'Profit over the ten-year hold',
      E10: '—',
      F10: "='Build to rent'!B28",
    });
    bold(inputs, ['A1', 'A4', 'A12', 'A20', 'B20', 'A22', 'A26', 'D4', 'E4', 'F4', 'D6', 'E6', 'F6']);
    format(inputs, 'B6:B8', 'number0');
    format(inputs, 'B9:B10', 'currency0');
    format(inputs, 'B13:B14', 'currency0');
    format(inputs, 'B15:B17', 'percent1');
    format(inputs, 'B19:B20', 'currency0');
    format(inputs, 'B23:B24', 'percent1');
    format(inputs, 'B27:B28', 'currency0');
    format(inputs, 'B29:B34', 'percent1');
    format(inputs, 'E5:F6', 'currency0');
    format(inputs, 'E7:F9', 'percent1');
    format(inputs, 'E10:F10', 'currency0');

    // ---- Build to sell -----------------------------------------------------
    Object.assign(sell.colWidths, { 1: 260, 2: 130 });
    put(sell, {
      A1: 'Build to sell',
      A2: 'Develop, sell every unit, take the profit at completion.',

      A4: 'Revenue',
      A5: 'Gross development value',
      B5: '=Inputs!B10',
      A6: 'Selling costs',
      B6: '=-B5*Inputs!B23',
      A7: 'Net sales',
      B7: '=B5+B6',

      A9: 'Costs',
      A10: 'Land',
      B10: '=-Inputs!B14',
      A11: 'Construction',
      B11: '=-Inputs!B9',
      A12: 'Professional fees',
      B12: '=B11*Inputs!B15',
      A13: 'Contingency',
      B13: '=B11*Inputs!B16',
      A14: 'Finance',
      B14: '=-Inputs!B19',
      A15: 'Total cost',
      B15: '=SUM(B10:B14)',

      A17: 'Result',
      A18: 'Profit',
      B18: '=B7+B15',
      A19: 'Profit on cost',
      B19: '=IFERROR(B18/-B15,"—")',
      A20: 'Margin on GDV',
      B20: '=IFERROR(B18/B5,"—")',
      A21: 'Profit per dwelling',
      B21: '=IFERROR(B18/Inputs!B6,"—")',
      // The most the land can cost and still hit the target, holding finance
      // where it is — close enough to price a bid, not to sign one.
      A22: 'Land value at target profit',
      B22: '=B7/(1+Inputs!B24)+SUM(B11:B14)',
      A23: 'Headroom over land cost',
      B23: '=B22+B10',
    });
    bold(sell, ['A1', 'A4', 'A7', 'B7', 'A9', 'A15', 'B15', 'A17', 'A18', 'B18', 'A22', 'B22']);
    format(sell, 'B5:B15', 'currency0');
    format(sell, 'B18', 'currency0');
    format(sell, 'B19:B20', 'percent1');
    format(sell, 'B21:B23', 'currency0');

    // ---- Build to rent -----------------------------------------------------
    Object.assign(rent.colWidths, { 1: 260, 2: 120 });
    for (let c = 3; c <= 12; c++) rent.colWidths[c] = 104;
    put(rent, {
      A1: 'Build to rent',
      A2: 'Develop, let, hold for ten years, then sell the stabilised building.',

      A4: 'Stabilised year one',
      A5: 'Gross rent',
      B5: '=Inputs!B6*Inputs!B27*12',
      A6: 'Other income',
      B6: '=Inputs!B6*Inputs!B28*12',
      A7: 'Vacancy and bad debt',
      B7: '=-(B5+B6)*Inputs!B29',
      A8: 'Effective income',
      B8: '=SUM(B5:B7)',
      A9: 'Operating costs',
      B9: '=-B8*Inputs!B30',
      A10: 'Net operating income',
      B10: '=B8+B9',

      A12: 'On completion',
      A13: 'Total development cost',
      B13: '=Inputs!B20',
      A14: 'Yield on cost',
      B14: '=IFERROR(B10/B13,"—")',
      A15: 'Value at exit yield',
      B15: '=IFERROR(B10/Inputs!B32,0)',
      A16: 'Profit at completion',
      B16: '=B15*(1-Inputs!B33)-B13',
      A17: 'Profit on cost',
      B17: '=IFERROR(B16/B13,"—")',

      A19: 'Ten-year hold',
      A20: 'Year',
      A21: 'Net operating income',
      A22: 'Development cost',
      A23: 'Sale, net of costs',
      A24: 'Net cash flow',

      A26: 'Unlevered IRR',
      B26: '=IFERROR(IRR(B24:L24),"—")',
      A27: 'NPV at discount rate',
      B27: '=B24+NPV(Inputs!B34,C24:L24)',
      A28: 'Profit over the hold',
      B28: '=SUM(B24:L24)',
      A29: 'Equity multiple',
      B29: '=IFERROR(SUM(C24:L24)/-B24,"—")',
    });
    // Year 0 is the build; the sale in year 10 is priced off year 11's income,
    // which is what a buyer is paying for.
    for (let year = 0; year <= 10; year++) {
      const col = String.fromCharCode(66 + year); // B..L
      put(rent, {
        [`${col}20`]: String(year),
        [`${col}21`]: year === 0 ? '0' : `=$B$10*POWER(1+Inputs!$B$31,${col}$20-1)`,
        [`${col}22`]: year === 0 ? '=-B13' : '0',
        [`${col}23`]: year === 10 ? `=$B$10*POWER(1+Inputs!$B$31,${col}$20)/Inputs!$B$32*(1-Inputs!$B$33)` : '0',
        [`${col}24`]: `=SUM(${col}21:${col}23)`,
      });
    }
    bold(rent, ['A1', 'A4', 'A10', 'B10', 'A12', 'A16', 'B16', 'A19', 'A20', 'A24', 'A26', 'B26']);
    format(rent, 'B5:B10', 'currency0');
    format(rent, 'B13', 'currency0');
    format(rent, 'B14', 'percent1');
    format(rent, 'B15:B16', 'currency0');
    format(rent, 'B17', 'percent1');
    format(rent, 'B20:L20', 'number0');
    format(rent, 'B21:L24', 'currency0');
    format(rent, 'B26', 'percent1');
    format(rent, 'B27:B28', 'currency0');
    format(rent, 'B29', 'number2');

    wb.charts['cht-btr'] = {
      id: 'cht-btr',
      sheetId: rent.id,
      type: 'bar',
      title: 'Build to rent: net cash flow by year',
      labelRange: 'B20:L20',
      dataRange: 'B24:L24',
      seriesInFirstRow: false,
      x: 40,
      y: 740,
      w: 720,
      h: 260,
    };

    wb.activeSheetId = inputs.id;
    return wb;
  },
};

// ---------------------------------------------------------------------------
// Hotel
// ---------------------------------------------------------------------------

/**
 * A hotel is priced on its trading, not its floor area: keys, rate and
 * occupancy make the revenue, a stack of percentages takes it down to net
 * operating income, and a yield turns that into value. Floor area only enters
 * through the key count.
 */
const hotel: Example = {
  id: 'hotel-appraisal',
  name: 'Hotel appraisal',
  summary: 'Keys from the model, ADR and occupancy to NOI, value against cost per key, and a five-year ramp-up.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    sheet.name = 'Hotel';
    Object.assign(sheet.colWidths, { 1: 250, 2: 120, 3: 24, 4: 60, 5: 90, 6: 90, 7: 120, 8: 120 });

    put(sheet, {
      A1: 'Hotel appraisal',
      A2: DISCLAIMER,

      A4: 'Keys',
      A5: 'Hotel usage',
      B5: 'Hotel',
      A6: `="Net area per key ("&${AREA_UNIT}&")"`,
      B6: '38',
      A7: 'Keys from the model',
      B7: '=IFERROR(ROUND(GI.TOTAL("netArea","usage="&B5)/B6,0),0)',
      A8: 'Keys if none are modelled',
      B8: '180',
      A9: 'Keys',
      B9: '=IF(B7>0,B7,B8)',

      A11: 'Assumptions',
      A12: 'Average daily rate',
      B12: '220',
      A13: 'Stabilised occupancy',
      B13: '76%',
      A14: 'Food and beverage (% of rooms)',
      B14: '28%',
      A15: 'Other revenue (% of rooms)',
      B15: '6%',
      A16: 'Departmental costs (% of revenue)',
      B16: '32%',
      A17: 'Undistributed costs (% of revenue)',
      B17: '24%',
      A18: 'Management fees (% of revenue)',
      B18: '3%',
      A19: 'FF&E reserve (% of revenue)',
      B19: '4%',
      A20: 'Exit yield',
      B20: '7%',
      A21: 'Land per key',
      B21: '40000',
      A22: 'Construction per key',
      B22: '250000',
      A23: 'FF&E and pre-opening per key',
      B23: '35000',
      A24: 'Fees and contingency',
      B24: '15%',
      A25: 'Finance rate (annual)',
      B25: '7%',
      A26: 'Build period (months)',
      B26: '30',
      A27: 'ADR growth (annual)',
      B27: '3%',

      A29: 'Stabilised year',
      A30: 'RevPAR',
      B30: '=B12*B13',
      A31: 'Rooms revenue',
      B31: '=B9*B30*365',
      A32: 'Food and beverage',
      B32: '=B31*B14',
      A33: 'Other revenue',
      B33: '=B31*B15',
      A34: 'Total revenue',
      B34: '=SUM(B31:B33)',
      A35: 'Departmental costs',
      B35: '=-B34*B16',
      A36: 'Undistributed costs',
      B36: '=-B34*B17',
      A37: 'Gross operating profit',
      B37: '=B34+B35+B36',
      A38: 'Management fees',
      B38: '=-B34*B18',
      A39: 'FF&E reserve',
      B39: '=-B34*B19',
      A40: 'Net operating income',
      B40: '=B37+B38+B39',
      A41: 'NOI margin',
      B41: '=IFERROR(B40/B34,"—")',

      A43: 'Value and cost',
      A44: 'Value at exit yield',
      B44: '=IFERROR(B40/B20,0)',
      A45: 'Value per key',
      B45: '=IFERROR(B44/B9,"—")',
      A46: 'Land',
      B46: '=B9*B21',
      A47: 'Construction',
      B47: '=B9*B22',
      A48: 'FF&E and pre-opening',
      B48: '=B9*B23',
      A49: 'Fees and contingency',
      B49: '=(B47+B48)*B24',
      A50: 'Finance',
      B50: '=(B46+(B47+B48+B49)/2)*B25*B26/12',
      A51: 'Total development cost',
      B51: '=SUM(B46:B50)',
      A52: 'Cost per key',
      B52: '=IFERROR(B51/B9,"—")',
      A53: 'Profit',
      B53: '=B44-B51',
      A54: 'Profit on cost',
      B54: '=IFERROR(B53/B51,"—")',
      A55: 'Yield on cost',
      B55: '=IFERROR(B40/B51,"—")',

      // A new hotel does not open stabilised. The ramp is a share of the
      // stabilised occupancy, written out so it can be edited year by year.
      D11: 'Ramp-up',
      D12: 'Year',
      E12: 'Occupancy',
      F12: 'ADR',
      G12: 'Revenue',
      H12: 'NOI',
    });
    const ramp = [0.8, 0.92, 1, 1, 1];
    ramp.forEach((share, i) => {
      const r = 13 + i;
      put(sheet, {
        [`D${r}`]: String(i + 1),
        [`E${r}`]: `=$B$13*${share}`,
        [`F${r}`]: `=$B$12*POWER(1+$B$27,D${r}-1)`,
        [`G${r}`]: `=$B$9*365*E${r}*F${r}*(1+$B$14+$B$15)`,
        [`H${r}`]: `=G${r}*(1-$B$16-$B$17-$B$18-$B$19)`,
      });
    });

    wb.charts['cht-hotel'] = {
      id: 'cht-hotel',
      sheetId: sheet.id,
      type: 'bar',
      title: 'Revenue and NOI, first five years',
      labelRange: 'D13:D17',
      dataRange: 'G12:H17',
      seriesInFirstRow: true,
      x: 400,
      y: 460,
      w: 440,
      h: 260,
    };

    bold(sheet, ['A1', 'A4', 'A9', 'B9', 'A11', 'A29', 'A34', 'A40', 'B40', 'A43', 'A51', 'A53', 'B53', 'D11', 'D12', 'E12', 'F12', 'G12', 'H12']);
    format(sheet, 'B6:B9', 'number0');
    format(sheet, 'B12', 'currency0');
    format(sheet, 'B13:B20', 'percent1');
    format(sheet, 'B21:B23', 'currency0');
    format(sheet, 'B24:B25', 'percent1');
    format(sheet, 'B27', 'percent1');
    format(sheet, 'B30', 'currency2');
    format(sheet, 'B31:B40', 'currency0');
    format(sheet, 'B41', 'percent1');
    format(sheet, 'B44:B53', 'currency0');
    format(sheet, 'B54:B55', 'percent1');
    format(sheet, 'E13:E17', 'percent1');
    format(sheet, 'F13:H17', 'currency0');
    return wb;
  },
};

// ---------------------------------------------------------------------------
// Data centre
// ---------------------------------------------------------------------------

/**
 * A data centre is priced per megawatt of IT load, not per square metre: the
 * rent is per kW, the build cost is per MW, and floor area only matters as the
 * room the load needs. Power itself is passed through to the tenant at cost, so
 * it sits outside the income here rather than inflating it.
 */
const dataCentre: Example = {
  id: 'data-centre-appraisal',
  name: 'Data centre appraisal',
  summary: 'IT load from the data hall area, rent per kW to NOI, value against cost per MW, and a five-year lease-up.',
  build() {
    const wb = createWorkbook();
    const sheet = wb.sheets[0]!;
    sheet.name = 'Data centre';
    Object.assign(sheet.colWidths, { 1: 260, 2: 120, 3: 24, 4: 60, 5: 90, 6: 120, 7: 120 });

    put(sheet, {
      A1: 'Data centre appraisal',
      A2: DISCLAIMER,

      A4: 'Capacity',
      A5: 'Data hall usage',
      B5: 'Data Centre',
      A6: `="IT load per "&${AREA_UNIT}&" of data hall (kW)"`,
      B6: '1.5',
      A7: 'IT load from the model (MW)',
      B7: '=IFERROR(GI.TOTAL("netArea","usage="&B5)*B6/1000,0)',
      A8: 'IT load if none is modelled (MW)',
      B8: '24',
      A9: 'IT load (MW)',
      B9: '=IF(B7>0,B7,B8)',
      A10: 'PUE',
      B10: '1.3',
      A11: 'Grid connection needed (MW)',
      B11: '=B9*B10',

      A13: 'Assumptions',
      A14: 'Rent per kW of IT per month',
      B14: '140',
      A15: 'Stabilised occupancy',
      B15: '95%',
      A16: 'Operating costs (% of rent)',
      B16: '12%',
      A17: 'Exit yield',
      B17: '6.5%',
      A18: 'Land and grid per MW',
      B18: '1500000',
      A19: 'Construction per MW',
      B19: '11000000',
      A20: 'Fees and contingency',
      B20: '12%',
      A21: 'Finance rate (annual)',
      B21: '7%',
      A22: 'Build period (months)',
      B22: '30',

      A24: 'Stabilised year',
      A25: 'Contracted rent',
      B25: '=B9*1000*B14*12*B15',
      A26: 'Operating costs',
      B26: '=-B25*B16',
      A27: 'Net operating income',
      B27: '=B25+B26',
      A28: 'Power is passed through to the tenant at cost and sits outside this income.',

      A30: 'Value and cost',
      A31: 'Value at exit yield',
      B31: '=IFERROR(B27/B17,0)',
      A32: 'Value per MW',
      B32: '=IFERROR(B31/B9,"—")',
      A33: 'Land and grid',
      B33: '=B9*B18',
      A34: 'Construction',
      B34: '=B9*B19',
      A35: 'Fees and contingency',
      B35: '=B34*B20',
      A36: 'Finance',
      B36: '=(B33+(B34+B35)/2)*B21*B22/12',
      A37: 'Total development cost',
      B37: '=SUM(B33:B36)',
      A38: 'Cost per MW',
      B38: '=IFERROR(B37/B9,"—")',
      A39: 'Profit',
      B39: '=B31-B37',
      A40: 'Profit on cost',
      B40: '=IFERROR(B39/B37,"—")',
      A41: 'Yield on cost',
      B41: '=IFERROR(B27/B37,"—")',

      // Halls fill as tenants take them, so the first years run under the
      // stabilised occupancy. Each year's share is its own cell to edit.
      D13: 'Lease-up',
      D14: 'Year',
      E14: 'Occupied',
      F14: 'Rent',
      G14: 'NOI',
    });
    const leaseUp = [0.4, 0.7, 0.9, 1, 1];
    leaseUp.forEach((share, i) => {
      const r = 15 + i;
      put(sheet, {
        [`D${r}`]: String(i + 1),
        [`E${r}`]: `=$B$15*${share}`,
        [`F${r}`]: `=$B$9*1000*$B$14*12*E${r}`,
        [`G${r}`]: `=F${r}*(1-$B$16)`,
      });
    });

    wb.charts['cht-dc'] = {
      id: 'cht-dc',
      sheetId: sheet.id,
      type: 'bar',
      title: 'Rent and NOI through lease-up',
      labelRange: 'D15:D19',
      dataRange: 'F14:G19',
      seriesInFirstRow: true,
      x: 400,
      y: 500,
      w: 420,
      h: 260,
    };

    bold(sheet, ['A1', 'A4', 'A9', 'B9', 'A13', 'A24', 'A27', 'B27', 'A30', 'A37', 'A39', 'B39', 'D13', 'D14', 'E14', 'F14', 'G14']);
    format(sheet, 'B7:B9', 'number1');
    format(sheet, 'B10', 'number2');
    format(sheet, 'B11', 'number1');
    format(sheet, 'B14', 'currency0');
    format(sheet, 'B15:B17', 'percent1');
    format(sheet, 'B18:B19', 'currency0');
    format(sheet, 'B20:B21', 'percent1');
    format(sheet, 'B25:B27', 'currency0');
    format(sheet, 'B31:B39', 'currency0');
    format(sheet, 'B40:B41', 'percent1');
    format(sheet, 'E15:E19', 'percent0');
    format(sheet, 'F15:G19', 'currency0');
    return wb;
  },
};

export const UNDERWRITE_EXAMPLES: Example[] = [btsVsBtr, hotel, dataCentre];
